import React, { useMemo, memo } from 'react';
import { Card } from 'antd';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';
import type { PaymentMethodBreakdown } from '../../services/dashboardService';
import { DashboardCardHeader } from './DashboardCardHeader';
import { ChartSkeleton } from './ChartSkeleton';
import { DashboardEmptyState } from './DashboardEmptyState';

interface PaymentMethodBreakdownChartProps {
  data: PaymentMethodBreakdown;
  rangeLabel?: string;
  loading?: boolean;
}

const CHART_COLORS = [
  '#1890ff',
  '#52c41a',
  '#faad14',
  '#722ed1',
  '#eb2f96',
  '#13c2c2',
];

const PaymentMethodBreakdownChart: React.FC<
  PaymentMethodBreakdownChartProps
> = ({ data, rangeLabel, loading = false }) => {
  const chartData = useMemo(() => {
    if (!data || typeof data !== 'object') return [];
    const entries = Object.entries(data)
      .filter(([, v]) => typeof v === 'number' && v > 0)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
    return entries;
  }, [data]);

  if (loading) {
    return (
      <Card className="admin-payment-method-card">
        <DashboardCardHeader
          title="Phương thức thanh toán"
          subtitle={rangeLabel}
          infoTooltip="Phân bổ đơn hàng theo phương thức thanh toán trong khoảng thời gian đã chọn."
        />
        <ChartSkeleton height={260} />
      </Card>
    );
  }

  if (chartData.length === 0) {
    return (
      <Card className="admin-payment-method-card">
        <DashboardCardHeader
          title="Phương thức thanh toán"
          subtitle={rangeLabel}
          infoTooltip="Phân bổ đơn hàng theo phương thức thanh toán trong khoảng thời gian đã chọn."
        />
        <DashboardEmptyState
          message="Chưa có dữ liệu thanh toán trong khoảng thời gian đã chọn."
          suggestion="Thử mở rộng phạm vi ngày."
        />
      </Card>
    );
  }

  const total = chartData.reduce((s, d) => s + d.value, 0);

  return (
    <Card className="admin-payment-method-card">
      <DashboardCardHeader
        title="Phương thức thanh toán"
        subtitle={rangeLabel}
        infoTooltip="Phân bổ đơn hàng theo phương thức thanh toán trong khoảng thời gian đã chọn."
      />
      <div style={{ padding: '8px 0' }}>
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={85}
              paddingAngle={2}
              dataKey="value"
              nameKey="name"
              label={({ name, value }) => {
                const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                return `${name}: ${pct}%`;
              }}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={entry.name}
                  fill={CHART_COLORS[index % CHART_COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number, name: string) => {
                const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                return [`${value} đơn (${pct}%)`, name];
              }}
              contentStyle={{
                backgroundColor: 'rgba(255,255,255,0.98)',
                border: '1px solid #e8e8e8',
                borderRadius: 4,
              }}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
        <div
          style={{
            textAlign: 'center',
            marginTop: 8,
            fontSize: 13,
            color: '#666',
          }}
        >
          Tổng: <strong>{total.toLocaleString('vi-VN')}</strong> đơn
        </div>
      </div>
    </Card>
  );
};

export default memo(PaymentMethodBreakdownChart);
